/**
 * Her input, drawn: M1 slots and the option surfaces, with the keyboard rules
 * of surfaces.md §3 and the trace of §3.5.
 *
 * Keyboard-first. The first open slot is focused when the item renders; a
 * keystroke that lands with no slot focused is not lost, it goes to the slot
 * she was last in, logged with `slot: ""` first. A refused key does nothing on
 * screen and is kept in the trace, because a child typing `x` into a number is
 * telling us something. Nothing here judges; `wrong` only repaints what she
 * chose, in the rust, after the server said „Ne visai.".
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import type { ChildItem } from "../api";
import { Rich, texHtml } from "../../lib/math";
import type { Recorder } from "../trace";
import { noteKey } from "../env";
import type { KeyLike } from "./keys";
import {
  type Action,
  type Entry,
  type SlotState,
  type SlotSurface,
  applyClick,
  applyKey,
  applyPaste,
  applyUnitPick,
  buildInput,
  initial,
  isEmpty,
  slotSurface,
  unitMatches,
} from "./slots";
import { type OptState, optClick, optInput, optKey, optKind } from "./options";
import {
  type ScaffoldState,
  type Skeleton,
  firstEmpty,
  initialScaffold,
  openPaths,
  parseSkeleton,
  scaffoldClick,
  scaffoldInput,
  scaffoldKey,
  scaffoldPaste,
} from "./scaffold";

/** The client's surface registry rev, sent as `surface.rev` on every answer. */
export const SURFACE_REV = 1;

export interface SurfaceHandle {
  input(): Record<string, unknown>;
  empty(): boolean;
}

/** The chemistry scaffold's skeleton, when this item is one the client draws (mode `coef`). */
function skeletonOf(item: ChildItem): Skeleton | null {
  return item.surface.id === "chem.scaffold" && item.surface.mode === "coef" ? parseSkeleton(item.prompt, item.surface.mode) : null;
}

export function supported(item: ChildItem): boolean {
  return slotSurface(item.surface.id, item.surface.mode) !== null || optKind(item.surface.id, item.surface.mode) !== null || skeletonOf(item) !== null;
}

/** The prompt as drawn above the surface: a scaffold draws its skeleton itself, so it is not written twice. */
export function promptOf(item: ChildItem): string {
  return skeletonOf(item)?.prompt ?? item.prompt;
}

interface Props {
  item: ChildItem;
  recorder: Recorder;
  live: boolean;
  wrong: boolean;
  test: boolean;
  onAction: (a: Exclude<Action, null>) => void;
  handle: (h: SurfaceHandle) => void;
}

function keyLike(e: KeyboardEvent | React.KeyboardEvent): KeyLike {
  return { key: e.key, code: e.code, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, repeat: e.repeat };
}

function isForeignControl(t: EventTarget | null, root: HTMLElement | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (root && root.contains(t)) return false;
  return ["BUTTON", "A", "INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
}

export function AnswerSurface(p: Props) {
  const slots = slotSurface(p.item.surface.id, p.item.surface.mode);
  if (slots) return <Slots {...p} surface={slots} />;
  const kind = optKind(p.item.surface.id, p.item.surface.mode);
  if (kind) return <Options {...p} kind={kind} />;
  const sk = skeletonOf(p.item);
  if (sk) return <Scaffold {...p} sk={sk} />;
  return (
    <p className="note">
      Šio klausimo atsakymo laukelio čia dar nėra ({p.item.surface.id} · {p.item.surface.mode}).
    </p>
  );
}

/* ------------------------------------------------------------------ slots */

function Slots({ item, recorder, live, wrong, test, onAction, handle, surface }: Props & { surface: SlotSurface }) {
  const [st, setSt] = useState<SlotState>(() => initial(surface));
  const stRef = useRef(st);
  stRef.current = st;
  const root = useRef<HTMLDivElement>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const units = item.options ?? [];

  useEffect(() => {
    handle({ input: () => buildInput(surface, stRef.current), empty: () => isEmpty(stRef.current) });
  }, [handle, surface]);

  // The caret and focus follow the model, never the other way round.
  useLayoutEffect(() => {
    if (!live) return;
    const el = inputs.current[st.active];
    if (!el) return;
    if (document.activeElement !== el) el.focus({ preventScroll: true });
    try {
      el.setSelectionRange(st.caret, st.caret);
    } catch {
      /* not a text input */
    }
  }, [st, live]);

  const onKey = useCallback(
    (e: KeyboardEvent, rerouted: boolean) => {
      noteKey(e.code);
      const k = keyLike(e);
      const cur = stRef.current;
      if (rerouted && k.key !== "Tab") recorder.push([k.code, k.key, "", "focus", "rerouted"]);
      const step = applyKey(surface, cur, k, { test, units });
      if (step.native) return;
      e.preventDefault();
      if (step.entry) recorder.push(step.entry);
      if (step.state !== cur) {
        stRef.current = step.state;
        setSt(step.state);
      }
      if (step.action) onAction(step.action);
    },
    [surface, recorder, test, units, onAction],
  );

  useEffect(() => {
    if (!live) return;
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const inside = root.current?.contains(e.target as Node) ?? false;
      if (!inside && isForeignControl(e.target, root.current)) {
        // Her Tab stop on the request button or „Baigti": Enter and Space there are that button's.
        if (["Enter", " ", "Tab", "Escape"].includes(e.key)) return;
      }
      onKey(e, !inside);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [live, onKey]);

  const box = (i: number, extra = "") => {
    const sl = surface.slots[i];
    const value = st.values[sl.id];
    const cls = `slot ${extra} ${st.active === i && live ? "active" : ""} ${wrong ? "wrong" : ""}`;
    return (
      <input
        key={sl.id}
        ref={(el) => {
          inputs.current[i] = el;
        }}
        className={cls}
        value={value}
        size={Math.max(extra.includes("slot-bracket") ? 1 : 3, [...value].length + 1)}
        aria-label={LABELS[sl.id] ?? "Atsakymas"}
        data-slot={sl.id}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        disabled={!live}
        onChange={() => {
          /* every change goes through the keydown model */
        }}
        onBeforeInput={(e) => e.preventDefault()}
        onPaste={(e) => {
          e.preventDefault();
          const step = applyPaste(surface, stRef.current, e.clipboardData.getData("text"));
          if (step.entry) recorder.push(step.entry);
          stRef.current = step.state;
          setSt(step.state);
        }}
        onMouseUp={(e) => {
          const caret = (e.target as HTMLInputElement).selectionStart ?? value.length;
          const step = applyClick(surface, stRef.current, i, caret);
          if (step.entry) recorder.push(step.entry);
          stRef.current = step.state;
          setSt(step.state);
        }}
      />
    );
  };

  const matches = surface.id === "qty" ? unitMatches(units, st.values.unit) : [];
  const picked = units.find((u) => u.id === st.unit);

  return (
    <div ref={root} className="surface" data-surface={`${surface.id}.${surface.mode}`}>
      {surface.id === "num" && surface.mode === "solve" && <span className="deco">x =</span>}
      {surface.id === "num" && box(0, surface.mode === "expr" ? "slot-wide" : "")}
      {surface.id === "num" && surface.mode === "pct" && <span className="deco">%</span>}

      {surface.id === "frac" && surface.mode === "mixed" && box(0)}
      {surface.id === "frac" && (
        <span className="frac">
          {box(surface.mode === "mixed" ? 1 : 0, "slot-small")}
          <span className="frac-bar" aria-hidden="true" />
          {box(surface.mode === "mixed" ? 2 : 1, "slot-small")}
        </span>
      )}

      {surface.id === "ratio" &&
        surface.slots.map((_, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {i > 0 && <span className="deco">:</span>}
            {box(i)}
          </span>
        ))}

      {surface.id === "stdform" && (
        <>
          {box(0)}
          <span className="deco">· 10</span>
          <span className="sup">{box(1, "slot-small")}</span>
        </>
      )}

      {surface.id === "point" && (
        <>
          <span className="deco">(</span>
          {box(0)}
          <span className="deco">;</span>
          {box(1)}
          <span className="deco">)</span>
        </>
      )}

      {surface.id === "interval" && (
        <>
          {box(0, "slot-bracket")}
          {box(1)}
          <span className="deco">;</span>
          {box(2)}
          {box(3, "slot-bracket")}
        </>
      )}

      {surface.id === "qty" && (
        <>
          {box(0)}
          <span className="unit-wrap">
            {box(1, picked ? "" : "slot-small")}
            {live && st.active === 1 && matches.length > 0 && (
              <span className="unit-list" role="listbox">
                {matches.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    className={`unit-opt ${u.id === st.unit || (i === 0 && !st.unit) ? "on" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const step = applyUnitPick(stRef.current, u.id);
                      if (step.entry) recorder.push(step.entry);
                      stRef.current = step.state;
                      setSt(step.state);
                    }}
                  >
                    <Rich text={u.label} />
                  </button>
                ))}
              </span>
            )}
          </span>
          {picked && (
            <span className="deco" aria-label="pasirinktas vienetas">
              <Rich text={picked.label} />
            </span>
          )}
        </>
      )}
    </div>
  );
}

const LABELS: Record<string, string> = {
  v: "Atsakymas",
  n: "Skaitiklis",
  d: "Vardiklis",
  w: "Sveikoji dalis",
  m: "Skaičius",
  e: "Laipsnio rodiklis",
  x: "x",
  y: "y",
  lb: "Kairysis skliaustas",
  lo: "Kairysis galas",
  hi: "Dešinysis galas",
  rb: "Dešinysis skliaustas",
  unit: "Matavimo vienetas",
};

/* ------------------------------------------------------------------ the chemistry scaffold (§4.1) */

function Scaffold({ item, recorder, live, wrong, test, onAction, handle, sk }: Props & { sk: Skeleton }) {
  const [st, setSt] = useState<ScaffoldState>(() => initialScaffold(sk));
  const stRef = useRef(st);
  stRef.current = st;
  const root = useRef<HTMLDivElement>(null);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const paths = openPaths(sk);

  useEffect(() => {
    // Not answerable until every box holds something: an empty coefficient is unanswered, never 1.
    handle({ input: () => scaffoldInput(sk, stRef.current), empty: () => firstEmpty(sk, stRef.current) !== null });
  }, [handle, sk]);

  useLayoutEffect(() => {
    if (!live) return;
    const el = inputs.current[st.active];
    if (!el) return;
    if (document.activeElement !== el) el.focus({ preventScroll: true });
    try {
      el.setSelectionRange(st.caret, st.caret);
    } catch {
      /* not a text input */
    }
  }, [st, live]);

  const apply = useCallback(
    (step: { state: ScaffoldState; entry: Entry | null; action: Action }) => {
      if (step.entry) recorder.push(step.entry);
      if (step.state !== stRef.current) {
        stRef.current = step.state;
        setSt(step.state);
      }
      if (step.action) onAction(step.action);
    },
    [recorder, onAction],
  );

  useEffect(() => {
    if (!live) return;
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const inside = root.current?.contains(e.target as Node) ?? false;
      if (!inside && isForeignControl(e.target, root.current) && ["Enter", " ", "Tab", "Escape"].includes(e.key)) return;
      noteKey(e.code);
      const k = keyLike(e);
      if (!inside && k.key !== "Tab") recorder.push([k.code, k.key, "", "focus", "rerouted"]);
      const step = scaffoldKey(sk, stRef.current, k, { test });
      if (step.native) return;
      e.preventDefault();
      apply(step);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [live, sk, test, recorder, apply]);

  const species = (side: Skeleton["left"]) =>
    side.map((sp, i) => {
      const idx = paths.indexOf(sp.path);
      const value = idx >= 0 ? (st.values[sp.path] ?? "") : sp.locked;
      return (
        <span key={sp.path} className="chem-term" data-term={sp.path}>
          {i > 0 && <span className="chem-plus" aria-hidden="true">+</span>}
          {idx >= 0 ? (
            <input
              ref={(el) => {
                inputs.current[idx] = el;
              }}
              className={`slot slot-coef ${st.active === idx && live ? "active" : ""} ${wrong ? "wrong" : ""}`}
              value={value}
              size={Math.max(1, [...value].length)}
              aria-label={`Koeficientas prieš ${sp.formula}`}
              data-slot={sp.path}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode="numeric"
              disabled={!live}
              onChange={() => {
                /* every change goes through the keydown model */
              }}
              onBeforeInput={(e) => e.preventDefault()}
              onPaste={(e) => {
                e.preventDefault();
                apply(scaffoldPaste(sk, stRef.current, e.clipboardData.getData("text")));
              }}
              onMouseUp={(e) => {
                const caret = (e.target as HTMLInputElement).selectionStart ?? value.length;
                apply(scaffoldClick(sk, stRef.current, idx, caret));
              }}
            />
          ) : (
            value && <span className="chem-locked">{value}</span>
          )}
          <span className="tex chem-formula" dangerouslySetInnerHTML={{ __html: texHtml(`\\ce{${sp.formula}}`) }} />
        </span>
      );
    });

  return (
    <div ref={root} className="surface chem-scaffold" data-surface={`${item.surface.id}.${item.surface.mode}`} role="group" aria-label="Lygtis su koeficientų langeliais">
      {species(sk.left)}
      <span className="tex chem-arrow" aria-label="virsta" dangerouslySetInnerHTML={{ __html: texHtml(`\\ce{${sk.arrow}}`) }} />
      {species(sk.right)}
    </div>
  );
}

/* ------------------------------------------------------------------ options */

function Options({ item, recorder, live, wrong, test, onAction, handle, kind }: Props & { kind: "one" | "many" | "order" }) {
  const [st, setSt] = useState<OptState>({ picked: [] });
  const stRef = useRef(st);
  stRef.current = st;
  const root = useRef<HTMLDivElement>(null);
  const opts = item.options ?? [];

  useEffect(() => {
    handle({ input: () => optInput(kind, stRef.current), empty: () => stRef.current.picked.length === 0 });
  }, [handle, kind]);

  useLayoutEffect(() => {
    if (live) root.current?.focus({ preventScroll: true });
    else if (document.activeElement === root.current) root.current?.blur();
  }, [live]);

  useEffect(() => {
    if (!live) return;
    const h = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const inside = root.current?.contains(e.target as Node) ?? false;
      if (!inside && isForeignControl(e.target, root.current) && ["Enter", " ", "Tab", "Escape"].includes(e.key)) return;
      noteKey(e.code);
      const step = optKey(kind, opts, stRef.current, keyLike(e), test);
      if (step.native) return;
      e.preventDefault();
      if (!inside && step.entry) recorder.push([e.code, e.key, "", "focus", "rerouted"]);
      if (step.entry) recorder.push(step.entry);
      stRef.current = step.state;
      setSt(step.state);
      if (step.action) onAction(step.action);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [live, kind, opts, recorder, test, onAction]);

  const click = (id: string) => {
    if (!live) return;
    const step = optClick(kind, stRef.current, id);
    if (step.entry) recorder.push(step.entry);
    stRef.current = step.state;
    setSt(step.state);
    root.current?.focus({ preventScroll: true });
  };

  return (
    <div
      ref={root}
      tabIndex={0}
      className={`options ${opts.length === 2 && kind !== "order" ? "two" : ""}`}
      role={kind === "one" ? "radiogroup" : "group"}
      aria-label="Atsakymo variantai"
      data-surface={`${item.surface.id}.${item.surface.mode}`}
    >
      {opts.map((o, i) => {
        const at = st.picked.indexOf(o.id);
        const on = at >= 0;
        return (
          <button
            key={o.id}
            type="button"
            tabIndex={-1}
            className={`option ${on ? "on" : ""} ${on && wrong ? "wrong" : ""}`}
            aria-pressed={on}
            data-option={o.id}
            disabled={!live}
            onClick={() => click(o.id)}
          >
            <span className="num">{kind === "order" && on ? at + 1 : i + 1}</span>
            <Rich text={o.label} />
          </button>
        );
      })}
      {kind === "order" && (
        <div className={`seq ${wrong ? "wrong" : ""}`} aria-label="Tavo eilė">
          {st.picked.map((id) => (
            <span key={id} className="seq-card">
              <Rich text={opts.find((o) => o.id === id)?.label ?? id} />
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
